import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard } from './KanbanCard';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { Modal } from './Modal';
import { LeadForm } from './LeadForm';
import { LeadDetailModal } from './LeadDetailModal';
import { BulkEditForm } from './BulkEditForm';
import { DashboardInsights } from './DashboardInsights';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getStatuses, getLeads, updateLeadStatus, getLead, deleteLead, bulkDeleteLeads, bulkUpdateLeads, getViews } from '../services/api';

// Map hex colors to color names for styling
const colorMap = {
  '#3B82F6': 'blue',
  '#F59E0B': 'yellow',
  '#8B5CF6': 'purple',
  '#F97316': 'orange',
  '#10B981': 'green',
  '#EF4444': 'red',
};

export const KanbanBoard = () => {
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('view');

  const [statuses, setStatuses] = useState([]);
  const [leads, setLeads] = useState([]);
  const [allLeads, setAllLeads] = useState([]); // Store all leads before filtering
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dragStartStatusId, setDragStartStatusId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [detailLeadModalOpen, setDetailLeadModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [bulkDeleteConfirmModalOpen, setBulkDeleteConfirmModalOpen] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const toast = useToast();
  const { t } = useLanguage();

  // Fetch statuses and leads on mount
  useEffect(() => {
    fetchData();
    fetchViews();
  }, []);

  // Apply view filter and search when view, allLeads, or searchQuery change
  useEffect(() => {
    applyViewFilter();
  }, [viewId, allLeads, views, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusesData, leadsData] = await Promise.all([
        getStatuses(),
        getLeads()
      ]);

      // Map statuses with color names
      const mappedStatuses = statusesData.map(status => ({
        id: status.id,
        name: status.status_name,
        color: colorMap[status.status_color] || 'blue',
        order: status.display_order,
      }));

      // Map leads with field_data
      const mappedLeads = leadsData.map(lead => ({
        id: lead.id,
        status_id: lead.status_id,
        full_name: lead.field_data?.full_name || 'Unnamed Lead',
        phone: lead.field_data?.phone,
        created_at: lead.created_at,
        action_date: lead.action_date,
        assigned_to: lead.assigned_to_name,
        field_data: lead.field_data, // Include full field_data for modal display
        source: lead.source,
      }));

      // Detect duplicates by phone number
      const phoneMap = new Map();
      mappedLeads.forEach(lead => {
        if (lead.phone) {
          // Normalize phone number
          const normalizedPhone = lead.phone.replace(/[\s\-\(\)]/g, '');
          if (!phoneMap.has(normalizedPhone)) {
            phoneMap.set(normalizedPhone, []);
          }
          phoneMap.get(normalizedPhone).push(lead.id);
        }
      });

      // Mark leads with duplicates
      const leadsWithDuplicates = mappedLeads.map(lead => {
        if (lead.phone) {
          const normalizedPhone = lead.phone.replace(/[\s\-\(\)]/g, '');
          const duplicateIds = phoneMap.get(normalizedPhone) || [];
          return {
            ...lead,
            hasDuplicate: duplicateIds.length > 1,
            duplicateCount: duplicateIds.length - 1,
          };
        }
        return { ...lead, hasDuplicate: false, duplicateCount: 0 };
      });

      setStatuses(mappedStatuses);
      setAllLeads(leadsWithDuplicates);
      setLeads(leadsWithDuplicates); // Initially show all leads
    } catch (error) {
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchViews = async () => {
    try {
      const response = await getViews();
      const viewsData = response.data || [];
      setViews(viewsData);
    } catch (error) {
      console.error('Failed to load views:', error);
    }
  };

  const applyViewFilter = () => {
    let filtered = [...allLeads];

    // Apply view filter
    if (viewId && views.length > 0) {
      const view = views.find(v => v.id === viewId);
      if (view && view.filter_criteria) {
        filtered = filtered.filter(lead => {
          // Check activity filter (campaign_name contains activity keyword)
          if (view.filter_criteria.activity) {
            const campaignName = lead.field_data?.campaign_name || '';
            if (!campaignName.includes(view.filter_criteria.activity)) {
              return false;
            }
          }
          return true;
        });
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(lead => {
        // Search in all text fields
        const searchableText = [
          lead.full_name,
          lead.phone,
          lead.field_data?.message,
          lead.field_data?.ad_name,
          lead.field_data?.campaign_name,
          lead.field_data?.adset_name,
          lead.source,
        ].filter(Boolean).join(' ').toLowerCase();

        return searchableText.includes(query);
      });
    }

    setLeads(filtered);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum 8px movement before drag activates
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-scroll when dragging near edges
  const handleAutoScroll = (clientX) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const scrollThreshold = 100; // pixels from edge to trigger scroll
    const scrollSpeed = 15; // pixels per interval

    // Clear any existing scroll interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    // Check if near right edge
    if (clientX > containerRect.right - scrollThreshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollLeft += scrollSpeed;
      }, 16); // ~60fps
    }
    // Check if near left edge
    else if (clientX < containerRect.left + scrollThreshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollLeft -= scrollSpeed;
      }, 16);
    }
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleDragStart = (event) => {
    const leadId = event.active.id;
    setActiveId(leadId);

    // Store the original status before dragging in state
    const lead = leads.find((l) => l.id === leadId);
    if (lead) {
      setDragStartStatusId(lead.status_id);
    }

    // Add mouse move listener for auto-scroll
    const handleMouseMove = (e) => {
      handleAutoScroll(e.clientX);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', (e) => {
      if (e.touches[0]) {
        handleAutoScroll(e.touches[0].clientX);
      }
    });

    // Store cleanup function
    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      stopAutoScroll();
    };

    // Store cleanup in a way we can access it later
    if (!window.__dragCleanup) {
      window.__dragCleanup = cleanup;
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeLead = leads.find((lead) => lead.id === activeId);
    const overLead = leads.find((lead) => lead.id === overId);

    if (!activeLead) return;

    // Dragging over a column
    const overStatus = statuses.find((status) => status.id === overId);
    if (overStatus) {
      setLeads((leads) => {
        return leads.map((lead) =>
          lead.id === activeId
            ? { ...lead, status_id: overStatus.id }
            : lead
        );
      });
      return;
    }

    // Dragging over another lead
    if (overLead && activeLead.status_id !== overLead.status_id) {
      setLeads((leads) => {
        return leads.map((lead) =>
          lead.id === activeId
            ? { ...lead, status_id: overLead.status_id }
            : lead
        );
      });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // Stop auto-scrolling and cleanup event listeners
    stopAutoScroll();
    if (window.__dragCleanup) {
      window.__dragCleanup();
      window.__dragCleanup = null;
    }

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    const activeLead = leads.find((lead) => lead.id === activeId);
    const overLead = leads.find((lead) => lead.id === overId);

    if (!activeLead) {
      setActiveId(null);
      return;
    }

    // Get the original status from when drag started (from state)
    const oldStatusId = dragStartStatusId || activeLead.status_id;

    // Determine the new status ID
    let newStatusId = activeLead.status_id; // Use the current status after drag over

    // Check if dropped on a column (status)
    const overStatus = statuses.find((status) => status.id === overId);
    if (overStatus) {
      newStatusId = overStatus.id;
    } else if (overLead) {
      // Dropped on another lead, use that lead's status
      newStatusId = overLead.status_id;
    }

    if (activeId !== overId && overLead) {
      setLeads((leads) => {
        const oldIndex = leads.findIndex((lead) => lead.id === activeId);
        const newIndex = leads.findIndex((lead) => lead.id === overId);

        return arrayMove(leads, oldIndex, newIndex);
      });
    }

    setActiveId(null);
    setDragStartStatusId(null); // Clear the stored status

    // Update lead status via API if status changed
    if (oldStatusId !== newStatusId) {
      try {
        await updateLeadStatus(activeId, newStatusId);
        toast.success(t('leadStatusUpdated'));
        // No need to refetch - the state is already updated by handleDragOver
      } catch (error) {
        toast.error(t('failedToUpdate'));
        // Revert the change by refetching
        fetchData();
      }
    }
  };

  const getLeadsByStatus = (statusId) => {
    return leads.filter((lead) => lead.status_id === statusId);
  };

  const handleOpenDetail = async (lead) => {
    try {
      // Fetch full lead data from API
      const fullLeadData = await getLead(lead.id);
      setSelectedLead(fullLeadData);
      setDetailLeadModalOpen(true);
    } catch (error) {
      toast.error(t('failedToLoadLeadDetails'));
    }
  };

  const handleCloseDetail = () => {
    setDetailLeadModalOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteClick = (lead) => {
    setLeadToDelete(lead);
    setDeleteConfirmModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return;

    try {
      await deleteLead(leadToDelete.id);
      toast.success(t('leadDeleted') || 'Lead deleted successfully');
      setDeleteConfirmModalOpen(false);
      setLeadToDelete(null);
      fetchData(); // Refresh the leads
    } catch (error) {
      toast.error(t('failedToDelete') || 'Failed to delete lead');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModalOpen(false);
    setLeadToDelete(null);
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedLeads(new Set());
  };

  const handleToggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allLeadIds = leads.map(lead => lead.id);
    setSelectedLeads(new Set(allLeadIds));
  };

  const handleDeselectAll = () => {
    setSelectedLeads(new Set());
  };

  const handleSelectAllInStatus = (statusId, leads) => {
    const leadsInStatus = leads.map(lead => lead.id);
    const allSelected = leadsInStatus.every(id => selectedLeads.has(id));

    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all in this status
        leadsInStatus.forEach(id => newSet.delete(id));
      } else {
        // Select all in this status
        leadsInStatus.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedLeads.size === 0) {
      toast.error(t('noLeadsSelected') || 'No leads selected');
      return;
    }
    setBulkDeleteConfirmModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const leadIds = Array.from(selectedLeads);
      const result = await bulkDeleteLeads(leadIds);

      if (result.data.success.length > 0) {
        toast.success(`${t('bulkDeleteSuccess')}: ${result.data.success.length}/${result.data.total}`);
      }

      if (result.data.failed.length > 0) {
        toast.error(`${t('bulkDeleteFailed')}: ${result.data.failed.length}/${result.data.total}`);
      }

      setBulkDeleteConfirmModalOpen(false);
      setSelectedLeads(new Set());
      setSelectionMode(false);
      fetchData();
    } catch (error) {
      toast.error(t('bulkDeleteFailed'));
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteConfirmModalOpen(false);
  };

  const handleBulkEditClick = () => {
    if (selectedLeads.size === 0) {
      toast.error(t('noLeadsSelected') || 'No leads selected');
      return;
    }
    setBulkEditModalOpen(true);
  };

  const handleBulkEditSubmit = async (updates, options = {}) => {
    try {
      const leadIds = Array.from(selectedLeads);
      const result = await bulkUpdateLeads(leadIds, updates, options);

      if (result.data.success.length > 0) {
        toast.success(`${t('bulkUpdateSuccess') || 'Successfully updated'} ${result.data.success.length} ${t('selectedLeads')}`);
      }

      if (result.data.failed.length > 0) {
        toast.error(`${t('bulkUpdatePartialFail') || 'Failed to update'} ${result.data.failed.length} ${t('selectedLeads')}`);
      }

      setBulkEditModalOpen(false);
      setSelectedLeads(new Set());
      setSelectionMode(false);
      await fetchData();
    } catch (error) {
      toast.error(t('bulkUpdateFailed') || 'Failed to update leads');
    }
  };

  const handleBulkEditCancel = () => {
    setBulkEditModalOpen(false);
  };

  const activeLead = activeId ? leads.find((lead) => lead.id === activeId) : null;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  const currentViewName = viewId ? views.find(v => v.id === viewId)?.view_name : null;
  const currentView = viewId ? views.find(v => v.id === viewId) : null;

  console.log('KanbanBoard - viewId:', viewId);
  console.log('KanbanBoard - currentView:', currentView);
  console.log('KanbanBoard - filter_criteria:', currentView?.filter_criteria);

  return (
    <div className="flex flex-col">
      {/* Dashboard Insights */}
      <DashboardInsights currentView={currentView} />

      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentViewName || t('leadPipeline')}
            </h2>
            <p className="text-gray-600 mt-1">
              {selectionMode
                ? `${selectedLeads.size} ${t('selectedLeads')}`
                : t('dragToUpdate')
              }
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
          {selectionMode ? (
            <>
              <Button variant="secondary" onClick={handleSelectAll}>
                {t('selectAll')}
              </Button>
              <Button variant="secondary" onClick={handleDeselectAll}>
                {t('deselectAll')}
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkEditClick}
                disabled={selectedLeads.size === 0}
              >
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('bulkEdit') || 'Bulk Edit'}
              </Button>
              <Button
                variant="danger"
                onClick={handleBulkDeleteClick}
                disabled={selectedLeads.size === 0}
              >
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('bulkDelete')}
              </Button>
              <Button variant="secondary" onClick={handleToggleSelectionMode}>
                {t('exitSelectionMode')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handleToggleSelectionMode}>
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('selectionMode')}
              </Button>
              <Button variant="primary" onClick={() => setAddLeadModalOpen(true)}>
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M12 4v16m8-8H4" />
                </svg>
                {t('addLead')}
              </Button>
            </>
          )}
        </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search') + '...'}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={scrollContainerRef}
            className="h-full overflow-x-auto pb-4 kanban-scroll-container"
          >
            <div className="flex gap-4 h-full">
              {statuses.map((status) => {
                const statusLeads = getLeadsByStatus(status.id);
                // Hide empty columns when searching
                if (searchQuery.trim() && statusLeads.length === 0) {
                  return null;
                }
                return (
                  <KanbanColumn
                    key={status.id}
                    status={status}
                    leads={statusLeads}
                    onOpenDetail={handleOpenDetail}
                    onDelete={handleDeleteClick}
                    selectionMode={selectionMode}
                    selectedLeads={selectedLeads}
                    onToggleSelection={handleToggleLeadSelection}
                    onSelectAllInStatus={handleSelectAllInStatus}
                  />
                );
              })}
            </div>
          </div>

          <DragOverlay>
            {activeLead ? (
              <KanbanCard lead={activeLead} isDragging />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <Modal
        isOpen={addLeadModalOpen}
        onClose={() => setAddLeadModalOpen(false)}
        title={t('addNewLead')}
      >
        <LeadForm
          onSuccess={() => {
            setAddLeadModalOpen(false);
            fetchData();
          }}
          onCancel={() => setAddLeadModalOpen(false)}
        />
      </Modal>

      <LeadDetailModal
        lead={selectedLead}
        isOpen={detailLeadModalOpen}
        onClose={handleCloseDetail}
        onUpdate={() => {
          fetchData();
          handleCloseDetail();
        }}
        onDelete={(lead) => {
          handleCloseDetail();
          handleDeleteClick(lead);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModalOpen}
        onClose={handleDeleteCancel}
        title={t('confirmDelete') || 'Confirm Delete'}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('confirmDeleteMessage') || 'Are you sure you want to delete this lead?'}
            {leadToDelete && (
              <span className="font-semibold"> {leadToDelete.full_name || leadToDelete.field_data?.full_name || 'this lead'}</span>
            )}?
          </p>
          <p className="text-sm text-gray-500">
            {t('deleteWarning') || 'This action cannot be undone.'}
          </p>
          <div className="flex gap-3 pt-4">
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              className="flex-1"
            >
              {t('delete') || 'Delete'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleDeleteCancel}
              className="flex-1"
            >
              {t('cancel') || 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={bulkDeleteConfirmModalOpen}
        onClose={handleBulkDeleteCancel}
        title={t('confirmDelete')}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('bulkDeleteConfirm')}
          </p>
          <p className="text-sm text-gray-500">
            {selectedLeads.size} {t('selectedLeads')}
          </p>
          <p className="text-sm text-gray-500">
            {t('deleteWarning')}
          </p>
          <div className="flex gap-3 pt-4">
            <Button
              variant="danger"
              onClick={handleBulkDeleteConfirm}
              className="flex-1"
            >
              {t('delete')}
            </Button>
            <Button
              variant="secondary"
              onClick={handleBulkDeleteCancel}
              className="flex-1"
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Edit Modal */}
      <Modal
        isOpen={bulkEditModalOpen}
        onClose={handleBulkEditCancel}
        title={t('bulkEdit') || 'Bulk Edit Leads'}
      >
        <BulkEditForm
          onSubmit={handleBulkEditSubmit}
          onCancel={handleBulkEditCancel}
          selectedCount={selectedLeads.size}
        />
      </Modal>
    </div>
  );
};
